<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'

import DikeSectionUploadShapeFileStep from '@/components/dikeSection/DikeSectionUploadShapeFileStep.vue'
import DikeReferencePointsUploadShapeFileStep from '@/components/dike/DikeReferencePointsUploadShapeFileStep.vue'
import DikeProfileUploadShapeFileStep from '@/components/dikeProfile/DikeProfileUploadShapeFileStep.vue'
import DikeSectionWatersideStep from '@/components/dikeSection/DikeSectionWatersideStep.vue'
import AvatarLoader from '@/components/dike/AvatarLoader.vue'

const store = useStore()

const step = ref(1)

const dikeSectionLoaded = computed(() => store.state.dikeSection.loaded)
const dikeSectionLoading = computed(() => store.state.dikeSection.loading)
const referencePointLoaded = computed(() => store.state.referencePoint.loaded)
const referencePointLoading = computed(() => store.state.referencePoint.loading)
const dikeProfileLoading = computed(() => store.state.dikeProfiles.loading)
const dikeProfileLoaded = computed(() => store.state.dikeProfiles.loaded)
const commentLoading = computed(() => store.state.comment.loading)
const yearsLoading = computed(() => store.state.years.loading)

const isLoading = computed(
  () =>
    commentLoading.value ||
    dikeSectionLoading.value ||
    referencePointLoading.value ||
    dikeProfileLoading.value ||
    yearsLoading.value
)

const toggleEdit = (payload) => store.dispatch('project/toggleEdit', payload)

watch(dikeSectionLoaded, (val) => {
  if (val) {
    step.value += 2
  }
})

watch(referencePointLoaded, (val) => {
  if (val) {
    step.value += 1
  }
})

watch(dikeProfileLoaded, (val) => {
  if (val) {
    toggleEdit(false)
  }
})
</script>

<template>
  <v-card variant="flat" class="fill-height">
    <v-card v-if="isLoading" variant="flat" class="fill-height d-flex justify-center align-center">
      <v-container class="text-center">
        <v-progress-circular indeterminate color="primary" />
        <span class="text-center mt-4 v-card-subtitle"> Loading dike trajectory... </span>
      </v-container>
    </v-card>

    <template v-else>
      <v-tabs mandatory color="primary" editable elevation="0" v-model="step" center-active stacked>
        <v-tab :value="1" rounded="0">
          <AvatarLoader :loading="dikeSectionLoading" :value="1" />
          {{ dikeSectionLoading ? 'Loading' : 'Create' + ' dike trajectory' }}
        </v-tab>
        <v-tab :disabled="!dikeSectionLoaded" :value="2" rounded="0">
          <v-avatar color="primary" size="20" class="mb-2"> 2 </v-avatar>
          Select waterside
        </v-tab>
        <v-tab :value="3" :disabled="!dikeSectionLoaded" rounded="0">
          <AvatarLoader :loading="referencePointLoading" :value="3" />
          {{ referencePointLoading ? 'Loading' : 'Create' }} reference points
        </v-tab>
        <v-tab :disabled="!referencePointLoaded" :value="4" rounded="0">
          <AvatarLoader :loading="dikeProfileLoading" :value="4" />
          {{ dikeProfileLoading ? 'Loading' : 'Create' }} dike profiles
        </v-tab>
      </v-tabs>
      <v-window v-model="step">
        <DikeSectionUploadShapeFileStep :step="step" @continue="step = 2" />
        <DikeSectionWatersideStep :step="step" @continue="step = 3" @back="step = 1" />
        <DikeReferencePointsUploadShapeFileStep
          :step="step"
          @continue="step = 4"
          @back="step = 2"
        />

        <DikeProfileUploadShapeFileStep :step="step" @back="step = 3" @finish="toggleEdit()" />
      </v-window>
    </template>
  </v-card>
</template>

<style scoped>
.v-window {
  height: 92%;
  margin: 0;
}
</style>
